import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import BulkInviteModal from '../components/Insurer/BulkInviteModal';
import TokenTierSelect from '../components/Insurer/TokenTierSelect';
import { insurerService, BulkInviteStatusResponse, PartnerTokenTier } from '../services/insurerService';

type InviteRow = {
  id: string;
  email: string;
  tokenTier: PartnerTokenTier;
};

let inviteRowCounter = 0;

function createInviteRow(): InviteRow {
  inviteRowCounter += 1;
  return { id: `invite-row-${inviteRowCounter}`, email: '', tokenTier: 'simple' };
}

const BulkInvite: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'single' | 'multiple' | 'bulk'>('single');
  const [singleEmail, setSingleEmail] = useState('');
  const [singleTokenTier, setSingleTokenTier] = useState<PartnerTokenTier>('simple');
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([createInviteRow()]);
  const [bulkUploadTokenTier, setBulkUploadTokenTier] = useState<PartnerTokenTier>('simple');
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [status, setStatus] = useState<BulkInviteStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isInsurer = user?.role === 'INSURER';
  const isOrgAdmin = user?.org_role === 'org_admin';
  const [grantCreditOnInvite, setGrantCreditOnInvite] = useState(true);
  const MAX_POLL_ATTEMPTS = 150;

  React.useEffect(() => {
    if (taskId && isProcessing) {
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          clearInterval(interval);
          setIsProcessing(false);
          setError(
            'Invite processing is taking longer than expected. The background worker may be down — check with your administrator or retry later.'
          );
          return;
        }
        try {
          const statusData = await insurerService.getBulkInviteStatus(taskId);
          setStatus(statusData);

          if (statusData.status === 'completed' || statusData.status === 'failed') {
            clearInterval(interval);
            setIsProcessing(false);
          }
        } catch (err: any) {
          console.error('Error fetching status:', err);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [taskId, isProcessing]);

  const updateInviteRow = (id: string, patch: Partial<Pick<InviteRow, 'email' | 'tokenTier'>>) => {
    setInviteRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addInviteRow = () => {
    setInviteRows((rows) => [...rows, createInviteRow()]);
  };

  const removeInviteRow = (id: string) => {
    setInviteRows((rows) => (rows.length === 1 ? rows : rows.filter((row) => row.id !== id)));
  };

  if (!isInsurer) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="p-8 w-full max-w-md text-center bg-white rounded-lg shadow-lg">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">This page is only available to insurers.</p>
        </div>
      </div>
    );
  }

  if (!isOrgAdmin) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="p-8 w-full max-w-md text-center bg-white rounded-lg shadow-lg">
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Admin access required</h2>
          <p className="text-gray-600">
            Only organization admins can invite clients. Contact your org admin if you need someone added.
          </p>
        </div>
      </div>
    );
  }

  const handleSingleInvite = async () => {
    if (!singleEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setError(null);
    setIsProcessing(true);
    setStatus(null);

    try {
      const response = await insurerService.inviteClientsByEmail(
        [singleEmail.trim()],
        grantCreditOnInvite,
        singleTokenTier
      );
      setTaskId(response.task_id);
      setSingleEmail('');
      setSingleTokenTier('simple');
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
      setIsProcessing(false);
    }
  };

  const handleMultipleInvite = async () => {
    const validRows = inviteRows
      .map((row) => ({ email: row.email.trim(), token_tier: row.tokenTier }))
      .filter((row) => row.email.length > 0);

    if (validRows.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    setError(null);
    setIsProcessing(true);
    setStatus(null);

    try {
      const response = await insurerService.inviteClientsWithTiers(validRows, grantCreditOnInvite);
      setTaskId(response.task_id);
      setInviteRows([createInviteRow()]);
    } catch (err: any) {
      setError(err.message || 'Failed to send invitations');
      setIsProcessing(false);
    }
  };

  const handleBulkInvite = () => {
    setIsFileModalOpen(true);
  };

  const handleFileTaskStarted = (taskId: string) => {
    setTaskId(taskId);
    setStatus(null);
    setIsProcessing(true);
    setError(null);
  };

  const hasMultipleInviteRows = inviteRows.some((row) => row.email.trim().length > 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="mb-6 flex justify-between items-start gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invite Clients</h1>
              <p className="mt-2 text-sm text-gray-600">
                Send invitation emails to your clients. Each invite includes a registration link
                {grantCreditOnInvite ? ' and 1 tabletop token by default' : ''}.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/insurer-dashboard')}
              className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <label className="flex items-start gap-3 mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer">
            <input
              type="checkbox"
              checked={grantCreditOnInvite}
              onChange={(e) => setGrantCreditOnInvite(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              disabled={isProcessing}
            />
            <span className="text-sm text-blue-900">
              <span className="font-medium">Grant 1 tabletop token with each invite</span>
              <span className="block text-blue-800 mt-1">
                Recommended for Cowbell clients — they can start their exercise after registering.
                Uncheck if you only want to add clients without provisioning credits yet.
              </span>
            </span>
          </label>

          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('single')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'single'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Single Email
              </button>
              <button
                onClick={() => setActiveTab('multiple')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'multiple'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Multiple Emails
              </button>
              <button
                onClick={() => setActiveTab('bulk')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'bulk'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bulk Upload
              </button>
            </nav>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {activeTab === 'single' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="single-email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="single-email"
                  value={singleEmail}
                  onChange={(e) => setSingleEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  disabled={isProcessing}
                />
              </div>

              {grantCreditOnInvite && (
                <div>
                  <label htmlFor="single-token-tier" className="block text-sm font-medium text-gray-700 mb-2">
                    Token type
                  </label>
                  <TokenTierSelect
                    id="single-token-tier"
                    value={singleTokenTier}
                    onChange={setSingleTokenTier}
                    disabled={isProcessing}
                  />
                </div>
              )}

              <button
                onClick={handleSingleInvite}
                disabled={isProcessing || !singleEmail.trim()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          )}

          {activeTab === 'multiple' && (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                        Email
                      </th>
                      {grantCreditOnInvite && (
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 w-72">
                          Token type
                        </th>
                      )}
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500 w-20">
                        <span className="sr-only">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {inviteRows.map((row) => (
                      <tr key={row.id}>
                        <td className="px-4 py-3 align-top">
                          <input
                            type="email"
                            value={row.email}
                            onChange={(e) => updateInviteRow(row.id, { email: e.target.value })}
                            placeholder="client@example.com"
                            disabled={isProcessing}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                          />
                        </td>
                        {grantCreditOnInvite && (
                          <td className="px-4 py-3 align-top">
                            <TokenTierSelect
                              value={row.tokenTier}
                              onChange={(tokenTier) => updateInviteRow(row.id, { tokenTier })}
                              disabled={isProcessing}
                              showDescription={false}
                              compact
                              className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm bg-white disabled:opacity-50"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 align-top text-right">
                          <button
                            type="button"
                            onClick={() => removeInviteRow(row.id)}
                            disabled={isProcessing || inviteRows.length === 1}
                            className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                            aria-label="Remove row"
                          >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={addInviteRow}
                disabled={isProcessing}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-50"
              >
                + Add row
              </button>

              <button
                onClick={handleMultipleInvite}
                disabled={isProcessing || !hasMultipleInviteRows}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing ? 'Sending...' : 'Send Invitations'}
              </button>
            </div>
          )}

          {activeTab === 'bulk' && (
            <div className="space-y-4">
              {grantCreditOnInvite && (
                <div>
                  <label htmlFor="bulk-upload-token-tier" className="block text-sm font-medium text-gray-700 mb-2">
                    Token type
                  </label>
                  <TokenTierSelect
                    id="bulk-upload-token-tier"
                    value={bulkUploadTokenTier}
                    onChange={setBulkUploadTokenTier}
                    disabled={isProcessing}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Applies to every email in the uploaded file.
                  </p>
                </div>
              )}

              <div className="text-center py-8">
                <button
                  onClick={handleBulkInvite}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Upload File
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-blue-900 mb-2">File Format Requirements:</h3>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>CSV or Excel files (.csv, .xlsx, .xls)</li>
                  <li>Must contain an "email" column with valid email addresses</li>
                  <li>Other columns will be ignored</li>
                  <li>Duplicate emails will be automatically removed</li>
                </ul>
              </div>
            </div>
          )}

          {status && (
            <div className="mt-6 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Invitation Status</h3>

                {status.status === 'processing' && (
                  <div className="space-y-2">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                        style={{ width: `${status.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600">Processing... {status.progress}% complete</p>
                  </div>
                )}

                {status.status === 'completed' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">{status.successful}</div>
                        <div className="text-xs text-green-800">Successful</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-red-600">{status.failed}</div>
                        <div className="text-xs text-red-800">Failed</div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-yellow-600">{status.skipped}</div>
                        <div className="text-xs text-yellow-800">Skipped</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-blue-600">{status.credits_granted ?? 0}</div>
                        <div className="text-xs text-blue-800">Credits granted</div>
                      </div>
                    </div>

                    {status.skipped_details && status.skipped_details.length > 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                        <h4 className="text-xs font-medium text-yellow-900 mb-2">Skipped:</h4>
                        <ul className="text-xs text-yellow-900 space-y-1">
                          {status.skipped_details.slice(0, 15).map((row, index) => (
                            <li key={index}>
                              {row.email}: {row.reason.replace(/_/g, ' ')}
                            </li>
                          ))}
                          {status.skipped_details.length > 15 && (
                            <li className="text-gray-600">
                              ... and {status.skipped_details.length - 15} more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {status.errors && status.errors.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                        <h4 className="text-xs font-medium text-red-900 mb-2">Errors:</h4>
                        <ul className="text-xs text-red-800 space-y-1">
                          {status.errors.slice(0, 10).map((err, index) => (
                            <li key={index}>
                              {err.email && `${err.email}: `}
                              {err.error}
                            </li>
                          ))}
                          {status.errors.length > 10 && (
                            <li className="text-gray-600">... and {status.errors.length - 10} more errors</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <button
                      onClick={() => {
                        setStatus(null);
                        setTaskId(null);
                        setIsProcessing(false);
                      }}
                      className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                    >
                      Close
                    </button>
                  </div>
                )}

                {status.status === 'failed' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">
                      {status.errors && status.errors.length > 0
                        ? status.errors[0].error
                        : 'An error occurred while processing invitations'}
                    </p>
                    <button
                      onClick={() => {
                        setStatus(null);
                        setTaskId(null);
                        setIsProcessing(false);
                      }}
                      className="mt-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors text-sm"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <BulkInviteModal
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onTaskStarted={handleFileTaskStarted}
        grantCredit={grantCreditOnInvite}
        tokenTier={bulkUploadTokenTier}
      />
    </div>
  );
};

export default BulkInvite;
